import {
  BackendViewNames,
  ProductViews,
} from 'src/app/entities/products/enums/product-views.enum';
import {
  ITableColumnConfig,
  TableConfigColumns,
} from 'src/app/entities/products/interfaces/table-column.interface';
import { CountryCode } from '@app-shared/enums/country-codes.enum';
import { MapWithDefault } from '@app-shared/functions/helpers.functions';

export enum TableColumnID {
  ProductID = 'productID',
  ProductName = 'productName',
  ProductStatus = 'productStatus',
  BackInStockDate = 'backInStockDate',
  ExpectedAvailability = 'expectedAvailabilityDate',
  SkuCode = 'skuCode',
  NationalCode = 'nationalCode',
  Manufacturer = 'manufacturer',
  StockOutDate = 'stockOutDate',
  NumberOfAlternatives = 'numberOfAlternatives',
  NumberOfCustomers = 'numberOfCustomers',
  Price = 'price',
  Checkbox = 'checkbox',
  ShortageDuration = 'shortageDuration',
  OrderCreationDate = 'orderCreationDate',
  AdditionalInformation = 'additionalInformation',
  CustomerGroup = 'customerGroup',
  ExpectedStockOutLength = 'expectedStockOutLength',
  RemainingStock = 'remainingStock',
  VipExpectedStockOutDate = 'vipExpectedStockoutDate',
  VipExpectedAvailabilityDate = 'vipExpectedAvailabilityDate',
  QuantityAvailableThoughWhs = 'quantityAvailableThoughWhs',
  CountryName = 'countryName',
}

export enum TableColumnLabel {
  ProductID = 'PRODUCT.TABLE.COLUMN_LABEL.PRODUCT_ID',
  ProductName = 'PRODUCT.TABLE.COLUMN_LABEL.PRODUCT_NAME',
  ProductStatus = 'PRODUCT.TABLE.COLUMN_LABEL.PRODUCT_STATUS',
  BackInStockDate = 'PRODUCT.TABLE.COLUMN_LABEL.BACK_IN_STOCK_DATE',
  ExpectedAvailability = 'PRODUCT.TABLE.COLUMN_LABEL.EXPECTED_AVAILABILITY',
  SkuCode = 'PRODUCT.TABLE.COLUMN_LABEL.SKU_CODE',
  NationalCode = 'PRODUCT.TABLE.COLUMN_LABEL.NATIONAL_CODE',
  Manufacturer = 'PRODUCT.TABLE.COLUMN_LABEL.MANUFACTURER',
  StockOutDate = 'PRODUCT.TABLE.COLUMN_LABEL.STOCKOUT_DATE',
  NumberOfAlternatives = 'PRODUCT.TABLE.COLUMN_LABEL.NUMBER_OF_ALTERNATIVES',
  NumberOfCustomers = 'PRODUCT.TABLE.COLUMN_LABEL.NUMBER_OF_CUSTOMERS',
  Price = 'GENERAL.PRICE',
  Checkbox = 'PRODUCT.TABLE.COLUMN_LABEL.CHECKBOX',
  ShortageDuration = 'PRODUCT.TABLE.COLUMN_LABEL.SHORTAGE_DURATION',
  OrderCreationDate = 'PRODUCT.TABLE.COLUMN_LABEL.ORDER_CREATION_DATE',
  AdditionalInformation = 'PRODUCT.TABLE.COLUMN_LABEL.ADDITIONAL_INFORMATION',
  CustomerGroup = 'PRODUCT.TABLE.COLUMN_LABEL.CUSTOMER_GROUP',
  ExpectedStockOutLength = 'PRODUCT.TABLE.COLUMN_LABEL.EXPECTED_STOCK_OUT_LENGTH',
  RemainingStock = 'PRODUCT.TABLE.COLUMN_LABEL.REMAINING_STOCK',
  VipExpectedStockOutDate = 'PRODUCT.TABLE.COLUMN_LABEL.VIP_EXPECTED_STOCKOUT_DATE',
  VipExpectedAvailabilityDate = 'PRODUCT.TABLE.COLUMN_LABEL.VIP_EXPECTED_AVAILABILITY_DATE',
  QuantityAvailableThoughWhs = 'PRODUCT.TABLE.COLUMN_LABEL.QUANTITY_AVAILABLE_THOUGH_WHS',
  CountryName = 'PRODUCT.TABLE.COLUMN_LABEL.COUNTRY',
}

/** Map for mapping the backend defined names of columns to the frontend column ID and label */
export const tableConfigToTableColumnIdAndLabelMap = new MapWithDefault<
  TableConfigColumns,
  [TableColumnID | null, TableColumnLabel | null]
>(
  [null, null],
  [
    ['product_id', [TableColumnID.ProductID, TableColumnLabel.ProductID]],
    ['product_name', [TableColumnID.ProductName, TableColumnLabel.ProductName]],
    [
      'product_status',
      [TableColumnID.ProductStatus, TableColumnLabel.ProductStatus],
    ],
    [
      'back_in_stock_date',
      [TableColumnID.BackInStockDate, TableColumnLabel.BackInStockDate],
    ],
    [
      'expected_availability',
      [
        TableColumnID.ExpectedAvailability,
        TableColumnLabel.ExpectedAvailability,
      ],
    ],
    ['country_name', [TableColumnID.CountryName, TableColumnLabel.CountryName]],
    ['sku_code', [TableColumnID.SkuCode, TableColumnLabel.SkuCode]],
    [
      'national_code',
      [TableColumnID.NationalCode, TableColumnLabel.NationalCode],
    ],
    [
      'manufacturer',
      [TableColumnID.Manufacturer, TableColumnLabel.Manufacturer],
    ],
    [
      'stockout_date',
      [TableColumnID.StockOutDate, TableColumnLabel.StockOutDate],
    ],
    [
      'number_of_alternatives',
      [
        TableColumnID.NumberOfAlternatives,
        TableColumnLabel.NumberOfAlternatives,
      ],
    ],
    [
      'number_of_customers',
      [TableColumnID.NumberOfCustomers, TableColumnLabel.NumberOfCustomers],
    ],
    ['price', [TableColumnID.Price, TableColumnLabel.Price]],
    [
      'shortage_duration',
      [TableColumnID.ShortageDuration, TableColumnLabel.ShortageDuration],
    ],
    [
      'order_creation_date',
      [TableColumnID.OrderCreationDate, TableColumnLabel.OrderCreationDate],
    ],
    [
      'additional_information',
      [
        TableColumnID.AdditionalInformation,
        TableColumnLabel.AdditionalInformation,
      ],
    ],
    [
      'customer_group',
      [TableColumnID.CustomerGroup, TableColumnLabel.CustomerGroup],
    ],
    [
      'expected_stockout_length',
      [
        TableColumnID.ExpectedStockOutLength,
        TableColumnLabel.ExpectedStockOutLength,
      ],
    ],
    [
      'remaining_stock',
      [TableColumnID.RemainingStock, TableColumnLabel.RemainingStock],
    ],
    [
      'vip_expected_stockout_date',
      [
        TableColumnID.VipExpectedStockOutDate,
        TableColumnLabel.VipExpectedStockOutDate,
      ],
    ],
    [
      'vip_expected_availability_date',
      [
        TableColumnID.VipExpectedAvailabilityDate,
        TableColumnLabel.VipExpectedAvailabilityDate,
      ],
    ],
    [
      'quantity_available_though_whs',
      [
        TableColumnID.QuantityAvailableThoughWhs,
        TableColumnLabel.QuantityAvailableThoughWhs,
      ],
    ],
  ],
);

export const tableColumnIdToTableConfigMap = new MapWithDefault<
  TableColumnID,
  TableConfigColumns | null
>(null, [
  [TableColumnID.ProductID, 'product_id'],
  [TableColumnID.ProductName, 'product_name'],
  [TableColumnID.ProductStatus, 'product_status'],
  [TableColumnID.BackInStockDate, 'back_in_stock_date'],
  [TableColumnID.ExpectedAvailability, 'expected_availability'],
  [TableColumnID.CountryName, 'country_name'],
  [TableColumnID.SkuCode, 'sku_code'],
  [TableColumnID.NationalCode, 'national_code'],
  [TableColumnID.Manufacturer, 'manufacturer'],
  [TableColumnID.StockOutDate, 'stockout_date'],
  [TableColumnID.NumberOfAlternatives, 'number_of_alternatives'],
  [TableColumnID.NumberOfCustomers, 'number_of_customers'],
  [TableColumnID.Price, 'price'],
  [TableColumnID.ShortageDuration, 'shortage_duration'],
  [TableColumnID.OrderCreationDate, 'order_creation_date'],
  [TableColumnID.AdditionalInformation, 'additional_information'],
  [TableColumnID.CustomerGroup, 'customer_group'],
  [TableColumnID.ExpectedStockOutLength, 'expected_stockout_length'],
  [TableColumnID.RemainingStock, 'remaining_stock'],
  [TableColumnID.VipExpectedStockOutDate, 'vip_expected_stockout_date'],
  [TableColumnID.VipExpectedAvailabilityDate, 'vip_expected_availability_date'],
  [TableColumnID.QuantityAvailableThoughWhs, 'quantity_available_though_whs'],
]);

export const viewToTableConfigTabNameMap = new Map<
  ProductViews,
  ITableColumnConfig['tab_name']
>([
  [ProductViews.ALL, BackendViewNames.ALL],
  [ProductViews.SHORTAGE, BackendViewNames.SHORTAGE],
  [ProductViews.BACK_IN_STOCK, BackendViewNames.BACK_IN_STOCK],
  [ProductViews.NOVELTIES, BackendViewNames.NOVELTIES],
  [ProductViews.ORDERS, BackendViewNames.ORDERS],
  [ProductViews.EXPECTED_STOCK_OUT, BackendViewNames.EXPECTED_STOCK_OUT],
  [ProductViews.SORTALYS, BackendViewNames.SORTALYS],
]);

function createOrderMap<T>(items: T[]): Map<T, number> {
  const map = new Map<T, number>();

  items.forEach((itm, i) => map.set(itm, i));

  return map;
}

const DEFAULT_VIEW_ALL_COLUMN_ORDER: TableColumnID[] = [
  TableColumnID.ProductID,
  TableColumnID.ProductName,
  TableColumnID.ProductStatus,
  TableColumnID.NationalCode,
  TableColumnID.SkuCode,
  TableColumnID.Manufacturer,
  TableColumnID.NumberOfAlternatives,
  TableColumnID.ShortageDuration,
];

const DEFAULT_VIEW_SHORTAGE_ORDER: TableColumnID[] = [
  TableColumnID.ProductID,
  TableColumnID.ProductName,
  TableColumnID.ProductStatus,
  TableColumnID.StockOutDate,
  TableColumnID.ExpectedAvailability,
  TableColumnID.NationalCode,
  TableColumnID.SkuCode,
  TableColumnID.Manufacturer,
  TableColumnID.NumberOfAlternatives,
  TableColumnID.ShortageDuration,
];

const DEFAULT_VIEW_EXPECTED_SHORTAGE_ORDER: TableColumnID[] = [
  TableColumnID.ProductID,
  TableColumnID.ProductName,
  TableColumnID.ProductStatus,
  TableColumnID.VipExpectedStockOutDate,
  TableColumnID.VipExpectedAvailabilityDate,
  TableColumnID.ExpectedStockOutLength,
  // TableColumnID.RemainingStock, hidden for now while we wait for data
  TableColumnID.NationalCode,
  TableColumnID.SkuCode,
  TableColumnID.Manufacturer,
  TableColumnID.NumberOfAlternatives,
];

const DEFAULT_VIEW_BACK_IN_STOCK_ORDER: TableColumnID[] = [
  TableColumnID.ProductID,
  TableColumnID.ProductName,
  TableColumnID.ProductStatus,
  TableColumnID.BackInStockDate,
  TableColumnID.NationalCode,
  TableColumnID.SkuCode,
  TableColumnID.Manufacturer,
  TableColumnID.NumberOfAlternatives,
  TableColumnID.ShortageDuration,
];

const DEFAULT_VIEW_ORDERS_ORDER: TableColumnID[] = [
  TableColumnID.Checkbox,
  TableColumnID.ProductID,
  TableColumnID.ProductName,
  TableColumnID.ProductStatus,
  TableColumnID.BackInStockDate,
  TableColumnID.NationalCode,
  TableColumnID.SkuCode,
  TableColumnID.Manufacturer,
  TableColumnID.NumberOfAlternatives,
  TableColumnID.ShortageDuration,
  TableColumnID.OrderCreationDate,
];

const DEFAULT_VIEW_SORTALYS_ORDER: TableColumnID[] = [
  TableColumnID.ProductID,
  TableColumnID.ProductName,
  TableColumnID.ProductStatus,
  TableColumnID.NumberOfCustomers,
  TableColumnID.NationalCode,
  TableColumnID.SkuCode,
  TableColumnID.Manufacturer,
  TableColumnID.NumberOfAlternatives,
  TableColumnID.ShortageDuration,
];

// #region View ALL
const viewAll_ByCountry_OrderMap = new MapWithDefault<
  CountryCode,
  Map<TableColumnID, number>
>(createOrderMap(DEFAULT_VIEW_ALL_COLUMN_ORDER));
viewAll_ByCountry_OrderMap.set(
  CountryCode.SPAIN,
  createOrderMap([...DEFAULT_VIEW_ALL_COLUMN_ORDER, TableColumnID.Price]),
);
viewAll_ByCountry_OrderMap.set(
  CountryCode.FRANCE,
  createOrderMap([...DEFAULT_VIEW_ALL_COLUMN_ORDER, TableColumnID.Price]),
);
viewAll_ByCountry_OrderMap.set(
  CountryCode.GERMANY,
  createOrderMap([
    ...DEFAULT_VIEW_ALL_COLUMN_ORDER,
    TableColumnID.AdditionalInformation,
    TableColumnID.CustomerGroup,
  ]),
);
viewAll_ByCountry_OrderMap.set(
  CountryCode.CZECH,
  createOrderMap([
    TableColumnID.NationalCode,
    TableColumnID.SkuCode,
    TableColumnID.ProductName,
    TableColumnID.ProductStatus,
    TableColumnID.Manufacturer,
    TableColumnID.QuantityAvailableThoughWhs,
    TableColumnID.NumberOfAlternatives,
    TableColumnID.ShortageDuration,
  ]),
);
viewAll_ByCountry_OrderMap.set(
  CountryCode.REGION,
  createOrderMap([
    TableColumnID.ProductID,
    TableColumnID.ProductName,
    TableColumnID.ProductStatus,
    TableColumnID.CountryName,
    TableColumnID.NationalCode,
    TableColumnID.SkuCode,
    TableColumnID.Manufacturer,
    TableColumnID.NumberOfAlternatives,
    TableColumnID.ShortageDuration,
  ]),
);
// #endregion

// #region View SHORTAGE
const viewShortage_ByCountry_OrderMap = new MapWithDefault<
  CountryCode,
  Map<TableColumnID, number>
>(createOrderMap(DEFAULT_VIEW_SHORTAGE_ORDER));
viewShortage_ByCountry_OrderMap.set(
  CountryCode.SPAIN,
  createOrderMap([...DEFAULT_VIEW_SHORTAGE_ORDER, TableColumnID.Price]),
);
viewShortage_ByCountry_OrderMap.set(
  CountryCode.FRANCE,
  createOrderMap([...DEFAULT_VIEW_SHORTAGE_ORDER, TableColumnID.Price]),
);
viewShortage_ByCountry_OrderMap.set(
  CountryCode.GERMANY,
  createOrderMap([
    ...DEFAULT_VIEW_SHORTAGE_ORDER,
    TableColumnID.AdditionalInformation,
    TableColumnID.CustomerGroup,
  ]),
);
viewShortage_ByCountry_OrderMap.set(
  CountryCode.CZECH,
  createOrderMap([
    TableColumnID.NationalCode,
    TableColumnID.SkuCode,
    TableColumnID.ProductName,
    TableColumnID.ProductStatus,
    TableColumnID.StockOutDate,
    TableColumnID.ExpectedAvailability,
    TableColumnID.Manufacturer,
    TableColumnID.QuantityAvailableThoughWhs,
    TableColumnID.NumberOfAlternatives,
    TableColumnID.ShortageDuration,
  ]),
);

viewShortage_ByCountry_OrderMap.set(
  CountryCode.REGION,
  createOrderMap([
    TableColumnID.ProductID,
    TableColumnID.ProductName,
    TableColumnID.ProductStatus,
    TableColumnID.StockOutDate,
    TableColumnID.ExpectedAvailability,
    TableColumnID.CountryName,
    TableColumnID.NationalCode,
    TableColumnID.SkuCode,
    TableColumnID.Manufacturer,
    TableColumnID.NumberOfAlternatives,
    TableColumnID.ShortageDuration,
  ]),
);
// #endregion

// #region View EXPECTED_SHORTAGE
const viewExpectedShortage_ByCountry_OrderMap = new MapWithDefault<
  CountryCode,
  Map<TableColumnID, number>
>(createOrderMap(DEFAULT_VIEW_EXPECTED_SHORTAGE_ORDER));

viewExpectedShortage_ByCountry_OrderMap.set(
  CountryCode.SWITZERLAND,
  createOrderMap([
    ...DEFAULT_VIEW_EXPECTED_SHORTAGE_ORDER,
    TableColumnID.CountryName,
  ]),
);

viewExpectedShortage_ByCountry_OrderMap.set(
  CountryCode.REGION,
  createOrderMap([
    ...DEFAULT_VIEW_EXPECTED_SHORTAGE_ORDER,
    TableColumnID.CountryName,
  ]),
);
// #endregion

// #region View VIEW_BACK_IN_STOCK
const viewBackInStock_ByCountry_OrderMap = new MapWithDefault<
  CountryCode,
  Map<TableColumnID, number>
>(createOrderMap(DEFAULT_VIEW_BACK_IN_STOCK_ORDER));
viewBackInStock_ByCountry_OrderMap.set(
  CountryCode.SPAIN,
  createOrderMap([...DEFAULT_VIEW_SHORTAGE_ORDER, TableColumnID.Price]),
);
viewBackInStock_ByCountry_OrderMap.set(
  CountryCode.FRANCE,
  createOrderMap([...DEFAULT_VIEW_SHORTAGE_ORDER, TableColumnID.Price]),
);
viewBackInStock_ByCountry_OrderMap.set(
  CountryCode.GERMANY,
  createOrderMap([
    ...DEFAULT_VIEW_SHORTAGE_ORDER,
    TableColumnID.AdditionalInformation,
    TableColumnID.CustomerGroup,
  ]),
);
viewBackInStock_ByCountry_OrderMap.set(
  CountryCode.CZECH,
  createOrderMap([
    TableColumnID.NationalCode,
    TableColumnID.SkuCode,
    TableColumnID.ProductName,
    TableColumnID.ProductStatus,
    TableColumnID.BackInStockDate,
    TableColumnID.Manufacturer,
    TableColumnID.QuantityAvailableThoughWhs,
    TableColumnID.NumberOfAlternatives,
    TableColumnID.ShortageDuration,
  ]),
);
viewBackInStock_ByCountry_OrderMap.set(
  CountryCode.REGION,
  createOrderMap([
    TableColumnID.ProductID,
    TableColumnID.ProductName,
    TableColumnID.ProductStatus,
    TableColumnID.VipExpectedStockOutDate,
    TableColumnID.VipExpectedAvailabilityDate,
    TableColumnID.ExpectedStockOutLength,
    // TableColumnID.RemainingStock, hidden for now while we wait for data
    TableColumnID.CountryName,
    TableColumnID.NationalCode,
    TableColumnID.SkuCode,
    TableColumnID.Manufacturer,
    TableColumnID.NumberOfAlternatives,
  ]),
);
// #endregion

// #region View VIEW_ORDERS
const viewOrders_ByCountry_OrderMap = new MapWithDefault<
  CountryCode,
  Map<TableColumnID, number>
>(createOrderMap(DEFAULT_VIEW_ORDERS_ORDER));
viewOrders_ByCountry_OrderMap.set(
  CountryCode.SPAIN,
  createOrderMap([...DEFAULT_VIEW_ORDERS_ORDER, TableColumnID.Price]),
);
viewOrders_ByCountry_OrderMap.set(
  CountryCode.FRANCE,
  createOrderMap([...DEFAULT_VIEW_ORDERS_ORDER, TableColumnID.Price]),
);
viewOrders_ByCountry_OrderMap.set(
  CountryCode.GERMANY,
  createOrderMap([
    ...DEFAULT_VIEW_ORDERS_ORDER,
    TableColumnID.AdditionalInformation,
    TableColumnID.CustomerGroup,
  ]),
);
viewOrders_ByCountry_OrderMap.set(
  CountryCode.CZECH,
  createOrderMap([
    TableColumnID.Checkbox,
    TableColumnID.NationalCode,
    TableColumnID.SkuCode,
    TableColumnID.ProductName,
    TableColumnID.ProductStatus,
    TableColumnID.BackInStockDate,
    TableColumnID.Manufacturer,
    TableColumnID.QuantityAvailableThoughWhs,
    TableColumnID.NumberOfAlternatives,
    TableColumnID.ShortageDuration,
    TableColumnID.OrderCreationDate,
  ]),
);

viewOrders_ByCountry_OrderMap.set(
  CountryCode.REGION,
  createOrderMap([
    TableColumnID.Checkbox,
    TableColumnID.ProductID,
    TableColumnID.ProductName,
    TableColumnID.ProductStatus,
    TableColumnID.BackInStockDate,
    TableColumnID.CountryName,
    TableColumnID.NationalCode,
    TableColumnID.SkuCode,
    TableColumnID.Manufacturer,
    TableColumnID.NumberOfAlternatives,
    TableColumnID.ShortageDuration,
    TableColumnID.OrderCreationDate,
  ]),
);
// #endregion

// #region Sortalys
const viewSortalys_ByCountry_OrderMap = new MapWithDefault<
  CountryCode,
  Map<TableColumnID, number>
>(createOrderMap(DEFAULT_VIEW_SORTALYS_ORDER));
viewAll_ByCountry_OrderMap.set(
  CountryCode.SPAIN,
  createOrderMap([...DEFAULT_VIEW_SORTALYS_ORDER, TableColumnID.Price]),
);
viewAll_ByCountry_OrderMap.set(
  CountryCode.FRANCE,
  createOrderMap([...DEFAULT_VIEW_SORTALYS_ORDER, TableColumnID.Price]),
);
viewAll_ByCountry_OrderMap.set(
  CountryCode.GERMANY,
  createOrderMap([
    ...DEFAULT_VIEW_SORTALYS_ORDER,
    TableColumnID.AdditionalInformation,
    TableColumnID.CustomerGroup,
  ]),
);
viewAll_ByCountry_OrderMap.set(
  CountryCode.CZECH,
  createOrderMap([
    TableColumnID.NationalCode,
    TableColumnID.SkuCode,
    TableColumnID.ProductName,
    TableColumnID.ProductStatus,
    TableColumnID.NumberOfCustomers,
    TableColumnID.Manufacturer,
    TableColumnID.QuantityAvailableThoughWhs,
    TableColumnID.NumberOfAlternatives,
    TableColumnID.ShortageDuration,
  ]),
);
viewOrders_ByCountry_OrderMap.set(
  CountryCode.REGION,
  createOrderMap([
    TableColumnID.ProductID,
    TableColumnID.ProductName,
    TableColumnID.ProductStatus,
    TableColumnID.NumberOfCustomers,
    TableColumnID.CountryName,
    TableColumnID.NationalCode,
    TableColumnID.SkuCode,
    TableColumnID.Manufacturer,
    TableColumnID.NumberOfAlternatives,
    TableColumnID.ShortageDuration,
  ]),
);
// #endregion

// #region NOVELTIES
// Default NOVELTIES order without VIP columns
const DEFAULT_VIEW_NOVELTIES_ORDER: TableColumnID[] = [
  TableColumnID.ProductID,
  TableColumnID.ProductName,
  TableColumnID.ProductStatus,
  TableColumnID.NationalCode,
  TableColumnID.SkuCode,
  TableColumnID.Manufacturer,
  TableColumnID.NumberOfAlternatives,
  TableColumnID.ShortageDuration,
];

// Create the novelties map with country-specific configurations
const viewNovelties_ByCountry_OrderMap = new MapWithDefault<
  CountryCode,
  Map<TableColumnID, number>
>(createOrderMap(DEFAULT_VIEW_NOVELTIES_ORDER));

// Add country-specific configurations, WITHOUT VIP columns
viewNovelties_ByCountry_OrderMap.set(
  CountryCode.SPAIN,
  createOrderMap([...DEFAULT_VIEW_NOVELTIES_ORDER, TableColumnID.Price]),
);

viewNovelties_ByCountry_OrderMap.set(
  CountryCode.FRANCE,
  createOrderMap([...DEFAULT_VIEW_NOVELTIES_ORDER, TableColumnID.Price]),
);

viewNovelties_ByCountry_OrderMap.set(
  CountryCode.GERMANY,
  createOrderMap([
    ...DEFAULT_VIEW_NOVELTIES_ORDER,
    TableColumnID.AdditionalInformation,
    TableColumnID.CustomerGroup,
  ]),
);

viewNovelties_ByCountry_OrderMap.set(
  CountryCode.CZECH,
  createOrderMap([
    TableColumnID.NationalCode,
    TableColumnID.SkuCode,
    TableColumnID.ProductName,
    TableColumnID.ProductStatus,
    TableColumnID.Manufacturer,
    TableColumnID.QuantityAvailableThoughWhs,
    TableColumnID.NumberOfAlternatives,
    TableColumnID.ShortageDuration,
  ]),
);

viewNovelties_ByCountry_OrderMap.set(
  CountryCode.REGION,
  createOrderMap([...DEFAULT_VIEW_NOVELTIES_ORDER, TableColumnID.CountryName]),
);
// #endregion

export const columnSortingMapByView = new Map<
  ITableColumnConfig['tab_name'],
  MapWithDefault<CountryCode, Map<TableColumnID, number>>
>([
  [BackendViewNames.ALL, viewAll_ByCountry_OrderMap],
  [BackendViewNames.SHORTAGE, viewShortage_ByCountry_OrderMap],
  [
    BackendViewNames.EXPECTED_STOCK_OUT,
    viewExpectedShortage_ByCountry_OrderMap,
  ],
  [BackendViewNames.BACK_IN_STOCK, viewBackInStock_ByCountry_OrderMap],
  [BackendViewNames.NOVELTIES, viewNovelties_ByCountry_OrderMap],
  [BackendViewNames.ORDERS, viewOrders_ByCountry_OrderMap],
  [BackendViewNames.SORTALYS, viewSortalys_ByCountry_OrderMap],
]);
